import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AutoCompleteDomains, ButtonBaseType, ID, Maybe } from 'src/models';
import { useAutocomplete } from 'src/hooks/useAutocomplete';
import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { P1, P2 } from 'src/components/Typography';
import { requestSingleCompany } from 'src/store/companies/companies.action';
import { getCompany } from 'src/store/companies/companies.getters';
import { getAppParams, getSignUpInviteId } from 'src/store/app/app.getters';
import { ifTrue } from 'src/tools/logic.tools';
import { getUserInvitation } from 'src/store/user/user.getters';

import classes from './CompaniesAutoComplete.module.scss';

interface Props {
  isProject?: boolean;
}

export function CompaniesAutoComplete({ isProject }: Props) {
  const {
    value,
    onChange,
    classNames,
    onSubmit = mockFn,
    onDiscard = mockFn,
    isDisabled,
    data: companyName,
    isAutoFocus,
    onClick,
    customization = {},
    actions = {},
    placeholder,
    isCloseButton,
  } = useContext(InputContext);

  const {
    renderDropdownLabel,
    addNew,
    isInline,
    isXSmall,
    isSmallMenu,
    dropdownMinWidth = 'container-width',
  } = customization;

  const { onAddCompany, onChangeInput } = actions;

  const [inputValue, setInputValue] = useState<Maybe<string>>(companyName);

  const dispatch = useDispatch();

  const invitation = useSelector(getUserInvitation);

  const subId = useSelector(getAppParams).subId ?? invitation?.project_id;

  const company = useSelector(getCompany);

  const ids = useMemo(() => ifTrue(isProject && subId, [subId as string]), [isProject, subId]);

  const [isOpen, setIsOpen] = useState(false);

  const inviteId = useSelector(getSignUpInviteId);

  const { results, isLoading, onChangeHandler, isRequested } = useAutocomplete(
    AutoCompleteDomains.COMPANIES,
    ids,
    inviteId,
  );

  const allResults = useMemo(() => {
    if (addNew && onAddCompany && isRequested) return [...results, { id: 'add', label: addNew }];

    return results;
  }, [addNew, onAddCompany, results, isRequested]);

  useEffect(() => {
    if (isDisabled) setInputValue(undefined);
  }, [isDisabled]);

  useEffect(() => {
    if (!value || typeof inputValue !== 'undefined') return;

    if (companyName) setInputValue(companyName);
    else if (company && company.id === value) setInputValue(company.name);
    else dispatch(requestSingleCompany(value));
  }, [company, companyName, dispatch, inputValue, value]);

  function handleOnAddCompany(id: ID, label: string) {
    if (onChangeInput) onChangeInput(label);

    setInputValue(label);

    onChange({ id, label }, true);
  }

  function handleOnItemClick(id: ID) {
    setIsOpen(false);

    if (addNew && id === 'add' && onAddCompany) onAddCompany(inputValue, handleOnAddCompany);
    else {
      const item = results.find(i => i.id === id);

      if (onChangeInput) onChangeInput(item?.label);

      setInputValue(item?.label);

      onChange(item, true);
    }
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnChange(userInput: string) {
    setIsOpen(true);
    onChange(undefined);
    setInputValue(userInput);
    onChangeHandler(userInput);

    if (onChangeInput) onChangeInput(userInput);
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) onDiscard();
  }

  function handleOnDiscard() {
    setIsOpen(false);

    onDiscard();
  }

  function handleOnSubmit() {
    setIsOpen(false);

    if (onSubmit) onSubmit();
  }

  function renderCustomLabel({ label, sub_label }: ButtonBaseType) {
    // if we nee to render 'addXXX' button at the end
    if (addNew && label === addNew) return <P1 className={classes['add-new']}>{addNew}</P1>;

    return (
      <P2>
        <em>{sub_label}</em>
        {label}
      </P2>
    );
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const iconId = baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down';

    return (
      <InputWithIcons
        className={classNames?.input}
        onChange={handleOnChange}
        onDiscard={handleOnDiscard}
        onSubmit={handleOnSubmit}
        onClick={getHandleOnClick(baseComponentProps.onClick)}
        isLoading={isLoading}
        value={inputValue}
        onFocus={baseComponentProps.onFocus}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        rightIconId={ifTrue(results.length && !isCloseButton, iconId)}
        isXSmall={!!isXSmall}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={allResults}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      isOpen={isOpen && !isLoading}
      onRequestOpenClose={handleOnRequestOpenClose}
      renderItemLabel={renderDropdownLabel ?? renderCustomLabel}
      isSmall={!!isSmallMenu}
      isInline={!!isInline}
      dropdownMinWidth={dropdownMinWidth}
    />
  );
}
